import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { PHONE } from "../lib/api";
import Layout from "../components/Layout";
import { CUSTOM_OPTIONS } from "../data/content";

const START = 175;
const PLANS = [
  { k: "monthly", label: "Every month", min: 100, note: "$100 minimum per detail" },
  { k: "quarterly", label: "Every 3 months", min: 125, note: "$125 minimum per detail" },
];
const MODES = [
  { k: "keep", label: "Keep" },
  { k: "alternate", label: "Every other detail" },
  { k: "always", label: "Skip always" },
];

export default function CustomPlan() {
  const [plan, setPlan] = useState("");
  const [choices, setChoices] = useState({});
  const [desc, setDesc] = useState("");
  const [time, setTime] = useState("");

  const planMin = PLANS.find((p) => p.k === plan)?.min ?? 100;
  const visibleOptions = CUSTOM_OPTIONS.map((o, i) => ({ o, i })).filter(({ o }) => !o.monthlyOnly || plan === "monthly");
  const setMode = (i, mode) => setChoices((c) => ({ ...c, [i]: mode }));

  const total = useMemo(() => {
    let t = START;
    visibleOptions.forEach(({ o, i }) => {
      const mode = choices[i];
      if (mode === "always") t -= o.saveAlways;
      else if (mode === "alternate") t -= o.saveAlternate;
    });
    return Math.max(planMin, t);
  }, [choices, planMin, visibleOptions]);

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <p className="text-xs tracking-[0.3em] uppercase text-pink-500 font-bold mb-4">Customizable Plan</p>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter">Build your own plan</h1>
        <p className="text-zinc-400 mt-4 max-w-2xl text-lg">Start at a <strong>$175 premium detail</strong>, then for each service choose to keep it, skip it <strong>every other detail</strong> for a small saving, or <strong>skip it always</strong> for the full saving. First, choose how often you want your car serviced. Note: signature details cannot be customized.</p>

        <div className="mt-8">
          <p className="text-sm font-semibold mb-3">1. Choose your maintenance frequency *</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {PLANS.map((p) => (
              <button key={p.k} onClick={() => setPlan(p.k)} className={`text-left rounded-2xl border p-5 transition-all ${plan === p.k ? "border-pink-500 bg-[#1a1013]" : "border-white/10 bg-[#121212] hover:border-white/25"}`} data-testid={`plan-${p.k}`}>
                <p className="font-bold">{p.label}</p>
                <p className="text-sm text-pink-500 mt-1">{p.note}</p>
              </button>
            ))}
          </div>
        </div>

        {!plan ? (
          <p className="mt-6 text-amber-400 text-sm" data-testid="plan-gate">Select a frequency above to start customizing.</p>
        ) : (
          <>
            <div className="mt-8 space-y-3">
              <p className="text-sm font-semibold">2. Choose what to skip on each service</p>
              {visibleOptions.map(({ o, i }) => {
                const mode = choices[i] || "keep";
                return (
                  <div key={i} className="rounded-2xl border border-white/10 bg-[#121212] p-5" data-testid={`custom-opt-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold">{o.name}</p>
                        <p className="text-sm text-zinc-500 mt-1">Save ${o.saveAlternate}/detail (every other) or ${o.saveAlways}/detail (always){o.monthlyOnly ? " · monthly plans only" : ""}</p>
                      </div>
                      <div className="flex gap-1.5">
                        {MODES.map((m) => {
                          const disabled = m.k === "alternate" && o.saveAlternate === 0;
                          return (
                            <button key={m.k} disabled={disabled} onClick={() => setMode(i, m.k)} className={`text-xs rounded-full px-3 py-1.5 transition-colors ${mode === m.k ? "bg-pink-600 text-white" : "bg-white/5 border border-white/15 hover:border-white/30"} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`} data-testid={`custom-opt-${i}-${m.k}`}>{m.label}</button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 rounded-2xl border border-pink-500/40 bg-[#121212] p-6 flex items-center justify-between" data-testid="custom-price">
              <div><p className="text-sm text-zinc-400">Estimated plan price ({PLANS.find((p) => p.k === plan)?.label})</p><p className="text-4xl font-extrabold text-pink-500">${total}<span className="text-base text-zinc-500 font-normal"> / detail</span></p></div>
              <p className="text-xs text-zinc-500 max-w-[180px] text-right">${planMin} minimum spend applies</p>
            </div>
          </>
        )}

        <div className="mt-12 rounded-2xl border border-white/10 bg-[#121212] p-8">
          <h2 className="text-xl font-bold">Fully custom job?</h2>
          <p className="text-zinc-400 mt-2">Send a full job description and estimated time and we'll quote you directly.</p>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="Describe the job..." className="mt-4 w-full bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="custom-desc" />
          <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="Estimated time (e.g. 3 hours)" className="mt-3 w-full bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="custom-time" />
          <button onClick={() => { if (!desc) { toast.error("Add a job description first."); return; } toast.success("Request noted! Text us to finalize your custom quote."); }} className="mt-4 bg-pink-600 hover:bg-pink-500 text-white font-semibold rounded-full px-8 py-3 transition-colors active:scale-95" data-testid="custom-submit">Request custom quote</button>
          <p className="text-sm text-zinc-500 mt-3">Or text us at {PHONE}.</p>
        </div>
      </section>
    </Layout>
  );
}
